import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Users, CurrencyDollar, Package, ShoppingBag, ChartLine, Bank, Check, Pencil, Star } from "@phosphor-icons/react";
import OrderDetailsModal from "@/components/OrderDetailsModal";
import LiveAnalytics from "@/components/LiveAnalytics";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#FF3B00]" : "border-white/10"} p-6 bg-[#0A0A0A]`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">{label}</div>
          <div className="font-display text-4xl mt-2">{value}</div>
        </div>
        <Icon size={24} weight="bold" className={accent ? "text-[#FF3B00]" : "text-white/60"} />
      </div>
    </div>
  );
}

function PayModal({ row, onClose, onDone }) {
  const [method, setMethod] = useState(row.payout_info?.payout_method || "bank");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/payouts/${row.artist_id}/mark-paid`, { method, reference, note });
      toast.success(`Versement marqué: $${row.balance_due.toFixed(2)}`);
      onDone();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/20 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Confirmer le virement</div>
          <div className="font-display text-4xl uppercase mt-1">{row.artist_name}</div>
          <div className="mt-2 text-sm text-white/60">Montant à verser</div>
          <div className="font-display text-6xl text-[#FF3B00]">${row.balance_due.toFixed(2)}</div>
        </div>
        <div className="p-6 border-b border-white/10 space-y-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Coordonnées pilote</div>
            {row.payout_info?.payout_holder || row.payout_info?.payout_details ? (
              <div className="border border-white/10 p-3 bg-black text-sm">
                <div><span className="text-white/40">Méthode:</span> {row.payout_info?.payout_method || "—"}</div>
                <div><span className="text-white/40">Titulaire:</span> {row.payout_info?.payout_holder || "—"}</div>
                <pre className="mt-2 whitespace-pre-wrap font-mono text-xs">{row.payout_info?.payout_details}</pre>
                {row.payout_info?.payout_notes && <div className="mt-2 text-xs text-white/50">Notes: {row.payout_info.payout_notes}</div>}
              </div>
            ) : (
              <div className="border border-yellow-500 text-yellow-400 p-3 text-sm">L'pilote n'a pas renseigné ses coordonnées de paiement.</div>
            )}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">Méthode utilisée</label>
            <input value={method} onChange={(e) => setMethod(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">Référence / N° transaction</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="pay-reference"
              placeholder="Ex: WISE-8873AB"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/40">Note (optionnel)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="p-6 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-white/20 hover:border-white uppercase tracking-widest">Annuler</button>
          <button onClick={confirm} disabled={loading} data-testid="pay-confirm"
            className="flex-1 py-3 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
            {loading ? "..." : "Marquer comme payé"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommissionCell({ artist, defaultRate, onSaved }) {
  const [editing, setEditing] = useState(false);
  const currentPct = artist.commission_rate != null ? (artist.commission_rate * 100) : (defaultRate * 100);
  const isCustom = artist.commission_rate != null;
  const [val, setVal] = useState(currentPct.toString());
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0 || num > 50) return toast.error("Taux entre 0 et 50%");
    setSaving(true);
    try {
      await api.post(`/admin/artists/${artist.id}/commission`, { commission_rate: num / 100 });
      toast.success(`Commission mise à jour: ${num}%`);
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/artists/${artist.id}/commission`, { commission_rate: null });
      toast.success("Retour au taux par défaut");
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input type="number" step="0.5" min="0" max="50" value={val} onChange={(e) => setVal(e.target.value)}
          className="w-16 bg-transparent border border-[#FF3B00] outline-none px-2 py-1 text-sm" autoFocus />
        <span className="text-xs text-white/40">%</span>
        <button onClick={save} disabled={saving} data-testid={`commission-save-${artist.id}`}
          className="text-xs uppercase text-[#FF3B00] hover:underline ml-1">OK</button>
        <button onClick={() => { setVal(currentPct.toString()); setEditing(false); }} className="text-xs text-white/40 hover:text-white">×</button>
        {isCustom && <button onClick={reset} className="text-xs text-white/40 hover:text-white ml-1" title="Retour au taux par défaut">↺</button>}
      </div>
    );
  }
  return (
    <button onClick={() => setEditing(true)} data-testid={`commission-edit-${artist.id}`}
      className={`text-sm ${isCustom ? "text-[#FF3B00] font-bold" : "text-white/70"} hover:text-white inline-flex items-center gap-1 group`}>
      {currentPct}% {isCustom && <span className="text-[10px] uppercase tracking-widest bg-[#FF3B00]/20 text-[#FF3B00] px-1">custom</span>}
      <Pencil size={12} className="opacity-0 group-hover:opacity-100" />
    </button>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [artists, setArtists] = useState([]);
  const [fans, setFans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payouts, setPayouts] = useState({ rows: [], history: [] });
  const [payingRow, setPayingRow] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);

  const fetchAll = async () => {
    const [ov, ar, fa, or, po] = await Promise.all([
      api.get("/admin/overview").then(r => r.data).catch(() => null),
      api.get("/admin/artists").then(r => r.data).catch(() => []),
      api.get("/admin/fans").then(r => r.data).catch(() => []),
      api.get("/admin/orders").then(r => r.data).catch(() => []),
      api.get("/admin/payouts").then(r => r.data).catch(() => ({ rows: [], history: [] })),
    ]);
    setOverview(ov); setArtists(ar); setFans(fa); setOrders(or); setPayouts(po);
  };

  useEffect(() => { fetchAll(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/artists/${id}/status?status=${status}`);
      toast.success("Statut mis à jour");
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const refund = async (id) => {
    if (!confirm("Confirmer le remboursement ?")) return;
    try {
      await api.post(`/admin/orders/${id}/refund`);
      toast.success("Remboursement effectué");
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggleFeatured = async (a) => {
    try {
      const next = !a.featured;
      await api.post(`/admin/artists/${a.id}/featured`, { featured: next });
      toast.success(next ? `${a.artist_name || a.name} mis en vedette` : `${a.artist_name || a.name} retiré de la vedette`);
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const deleteArtist = async (a) => {
    const label = a.artist_name || a.name || a.email;
    if (!window.confirm(`Supprimer définitivement "${label}" ?\n\nCela supprimera aussi tous ses produits. Les commandes historiques restent intactes.\n\nCette action est irréversible.`)) return;
    try {
      const { data } = await api.delete(`/admin/artists/${a.id}`);
      toast.success(`Pilote supprimé (${data.deleted_products || 0} produit(s) retiré(s))`);
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const deleteFan = async (f) => {
    const label = f.name || f.email;
    if (!window.confirm(`Supprimer définitivement le compte client "${label}" ?\n\nLes commandes historiques restent intactes.\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${f.id}`);
      toast.success(`Compte client supprimé`);
      fetchAll();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const totalDue = payouts.rows.reduce((s, r) => s + r.balance_due, 0);

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="border-b border-white/10 pb-6 mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00]">Command Center</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase mt-1">Admin</h1>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto">
        {[
          { k: "overview", label: "Vue globale" },
          { k: "artists", label: "Pilotes" },
          { k: "fans", label: "Clients" },
          { k: "orders", label: "Commandes" },
          { k: "payouts", label: "Paiements" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`px-6 py-3 uppercase tracking-widest text-xs whitespace-nowrap ${tab === t.k ? "border-b-2 border-[#FF3B00] text-white" : "text-white/50 hover:text-white"}`}>
            {t.label}{t.k === "payouts" && totalDue > 0 && <span className="ml-2 text-[#FF3B00]">${totalDue.toFixed(0)}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={ShoppingBag} label="Commandes" value={overview.total_orders} />
            <StatCard icon={CurrencyDollar} label="CA total" value={`$${overview.total_revenue}`} />
            <StatCard icon={ChartLine} label="Commissions (15%)" value={`$${overview.total_commission}`} accent />
            <StatCard icon={Users} label="Pilotes" value={overview.artist_count} />
            <StatCard icon={Package} label="Produits actifs" value={overview.product_count} />
          </div>

          <LiveAnalytics />

          {overview.pending_artists > 0 && (
            <div className="border border-yellow-500 p-4 bg-yellow-500/10 text-yellow-400">
              {overview.pending_artists} pilote(s) en attente de validation.
              <button onClick={() => setTab("artists")} className="ml-2 underline">Voir</button>
            </div>
          )}

          {totalDue > 0 && (
            <div className="border border-[#FF3B00] p-4 bg-[#FF3B00]/10 text-[#FF3B00] flex items-center justify-between">
              <span><Bank size={16} weight="bold" className="inline mr-2" /> ${totalDue.toFixed(2)} à verser aux pilotes ({payouts.rows.length} pilote{payouts.rows.length > 1 ? "s" : ""}).</span>
              <button onClick={() => setTab("payouts")} className="underline uppercase tracking-widest text-xs">Voir</button>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="border border-white/10 p-6 bg-[#0A0A0A]">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-4">Top pilotes</div>
              {overview.top_artists?.length === 0 ? <div className="text-white/50">Aucune donnée.</div> :
                overview.top_artists.map((a) => (
                  <div key={a.artist_id} className="flex justify-between py-2 border-b border-white/5">
                    <span>{a.artist_name}</span>
                    <span className="font-display text-xl">${a.revenue.toFixed(2)}</span>
                  </div>
                ))
              }
            </div>
            <div className="border border-white/10 p-6 bg-[#0A0A0A]">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-4">Top produits</div>
              {overview.top_products?.length === 0 ? <div className="text-white/50">Aucune donnée.</div> :
                overview.top_products.map((p) => (
                  <div key={p.id} className="flex justify-between py-2 border-b border-white/5">
                    <span>{p.name}</span>
                    <span className="font-display text-xl">{p.sales_count} ventes</span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {tab === "artists" && (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#0A0A0A]">
              <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                <th className="p-4">Surnom pilote</th>
                <th className="p-4">Email</th>
                <th className="p-4">Créé</th>
                <th className="p-4">Vedette</th>
                <th className="p-4">Commission</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {artists.map((a) => (
                <tr key={a.id} className="border-b border-white/5">
                  <td className="p-4">{a.artist_name || a.name}</td>
                  <td className="p-4 text-white/60">{a.email}</td>
                  <td className="p-4 text-white/60">{new Date(a.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-4">
                    <button
                      onClick={() => toggleFeatured(a)}
                      data-testid={`featured-toggle-${a.id}`}
                      title={a.featured ? "Retirer de la vedette" : "Mettre en vedette"}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 border transition-colors ${
                        a.featured
                          ? "border-[#FF3B00] bg-[#FF3B00]/10 text-[#FF3B00]"
                          : "border-white/20 text-white/50 hover:border-white hover:text-white"
                      }`}>
                      <Star size={14} weight={a.featured ? "fill" : "regular"} />
                      <span className="text-xs uppercase tracking-widest">{a.featured ? "En vedette" : "Standard"}</span>
                    </button>
                  </td>
                  <td className="p-4"><CommissionCell artist={a} defaultRate={0.15} onSaved={fetchAll} /></td>
                  <td className="p-4">
                    <span className={`text-xs uppercase px-2 py-1 ${
                      a.status === "approved" ? "bg-green-500/20 text-green-400" :
                      a.status === "suspended" ? "bg-red-500/20 text-red-400" :
                      "bg-yellow-500/20 text-yellow-400"
                    }`}>{a.status || "pending"}</span>
                  </td>
                  <td className="p-4">
                    {a.status !== "approved" && (
                      <button onClick={() => setStatus(a.id, "approved")} data-testid={`approve-${a.id}`}
                        className="text-xs uppercase tracking-widest text-[#FF3B00] hover:underline mr-3">Valider</button>
                    )}
                    {a.status !== "suspended" ? (
                      <button onClick={() => setStatus(a.id, "suspended")}
                        className="text-xs uppercase tracking-widest text-red-400 hover:underline mr-3">Suspendre</button>
                    ) : (
                      <button onClick={() => setStatus(a.id, "approved")}
                        className="text-xs uppercase tracking-widest text-white/60 hover:underline mr-3">Réactiver</button>
                    )}
                    <button onClick={() => deleteArtist(a)} data-testid={`delete-${a.id}`}
                      className="text-xs uppercase tracking-widest text-red-500 hover:underline">Supprimer</button>
                  </td>
                </tr>
              ))}
              {artists.length === 0 && <tr><td colSpan={7} className="p-16 text-center text-white/50">Aucun pilote.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "fans" && (
        <div className="border border-white/10" data-testid="admin-fans-tab">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#0A0A0A]">
              <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                <th className="p-4">Nom</th>
                <th className="p-4">Email</th>
                <th className="p-4">Commandes</th>
                <th className="p-4">Inscription</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {fans.map((f) => (
                <tr key={f.id} className="border-b border-white/5" data-testid={`fan-row-${f.id}`}>
                  <td className="p-4">{f.name || "—"}</td>
                  <td className="p-4 text-white/60">{f.email}</td>
                  <td className="p-4">{f.order_count}</td>
                  <td className="p-4 text-white/60">{f.created_at ? new Date(f.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="p-4">
                    <button onClick={() => deleteFan(f)} data-testid={`delete-fan-${f.id}`}
                      className="text-xs uppercase tracking-widest text-red-500 hover:underline">Supprimer</button>
                  </td>
                </tr>
              ))}
              {fans.length === 0 && <tr><td colSpan={5} className="p-16 text-center text-white/50">Aucun client inscrit.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#0A0A0A]">
              <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                <th className="p-4">Commande</th>
                <th className="p-4">Client</th>
                <th className="p-4">Total</th>
                <th className="p-4">Commission</th>
                <th className="p-4">Statut</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setViewingOrder(o)} data-testid={`admin-order-row-${o.id}`}>
                  <td className="p-4"><button className="text-[#FF3B00] font-mono hover:underline">#{o.id.slice(-6).toUpperCase()}</button></td>
                  <td className="p-4 text-white/60">{o.customer_details?.name || o.buyer_email}</td>
                  <td className="p-4">${o.subtotal?.toFixed(2)}</td>
                  <td className="p-4 text-[#FF3B00]">${o.commission?.toFixed(2)}</td>
                  <td className="p-4"><span className="text-xs uppercase">{o.status}</span></td>
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    {o.payment_status === "paid" && (
                      <button onClick={() => refund(o.id)} className="text-xs uppercase tracking-widest text-red-400 hover:underline">Rembourser</button>
                    )}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="p-16 text-center text-white/50">Aucune commande.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payouts" && (
        <div className="space-y-6" data-testid="admin-payouts">
          <div className="border border-[#FF3B00] p-6 bg-[#0A0A0A] flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40">Solde total à verser</div>
              <div className="font-display text-6xl mt-2 text-[#FF3B00]">${totalDue.toFixed(2)}</div>
              <div className="text-xs text-white/50 mt-1">Reversé à {payouts.rows.length} pilote(s) — commission plateforme 15%</div>
            </div>
            <Bank size={64} weight="bold" className="text-[#FF3B00]/40" />
          </div>

          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 bg-[#0A0A0A]">
                <tr className="text-left text-xs uppercase tracking-widest text-white/40">
                  <th className="p-4">Pilote</th>
                  <th className="p-4">Méthode</th>
                  <th className="p-4 min-w-[280px]">Détails du virement</th>
                  <th className="p-4">Solde dû</th>
                  <th className="p-4">Total encaissé</th>
                  <th className="p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {payouts.rows.map((r) => (
                  <tr key={r.artist_id} className={`border-b border-white/5 ${r.has_request ? "bg-[#FF3B00]/5" : ""}`} data-testid={`payout-row-${r.artist_id}`}>
                    <td className="p-4 align-top">
                      <div className="flex items-center gap-2">
                        <span>{r.artist_name}</span>
                        {r.has_request && (
                          <span className="inline-flex items-center gap-1 bg-[#FF3B00] text-white text-[9px] uppercase tracking-widest px-2 py-0.5 animate-pulse" data-testid={`request-badge-${r.artist_id}`}>
                            Demande de paiement
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/40">{r.email}</div>
                      {r.has_request && r.request_at && (
                        <div className="text-[10px] text-[#FF3B00] mt-1">Demandé le {new Date(r.request_at).toLocaleDateString("fr-FR")} · ${r.request_amount?.toFixed(2)}</div>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      {r.payout_info?.payout_method ? (
                        <div>
                          <div className="text-xs uppercase text-white/80 font-medium">{r.payout_info.payout_method}</div>
                          {r.payout_info.payout_holder && (
                            <div className="text-xs text-white/40 truncate max-w-[160px]">{r.payout_info.payout_holder}</div>
                          )}
                        </div>
                      ) : <span className="text-yellow-400 text-xs">Aucune méthode</span>}
                    </td>
                    <td className="p-4 align-top">
                      {r.payout_info?.payout_details ? (
                        <div className="space-y-2">
                          <pre className="bg-black border border-white/10 p-3 text-xs font-mono whitespace-pre-wrap break-all max-w-[320px] text-white/90" data-testid={`payout-details-${r.artist_id}`}>{r.payout_info.payout_details}</pre>
                          <button
                            type="button"
                            data-testid={`copy-details-${r.artist_id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(r.payout_info.payout_details);
                                toast.success("Coordonnées copiées");
                              } catch { toast.error("Impossible de copier"); }
                            }}
                            className="text-[10px] uppercase tracking-widest text-[#FF3B00] hover:underline">
                            Copier les coordonnées
                          </button>
                          {r.payout_info.payout_notes && (
                            <div className="text-[10px] text-white/40 italic">Note : {r.payout_info.payout_notes}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-yellow-400 text-xs italic">L'pilote n'a pas encore renseigné ses coordonnées</span>
                      )}
                    </td>
                    <td className="p-4 font-display text-2xl text-[#FF3B00] align-top">${r.balance_due.toFixed(2)}</td>
                    <td className="p-4 text-white/60 align-top">${r.total_earned.toFixed(2)}</td>
                    <td className="p-4 align-top">
                      <button
                        onClick={() => setPayingRow(r)}
                        disabled={r.balance_due <= 0}
                        data-testid={`mark-paid-${r.artist_id}`}
                        className="px-4 py-2 bg-[#FF3B00] text-white uppercase tracking-widest text-xs hover:bg-[#E03500] disabled:opacity-40 disabled:cursor-not-allowed">
                        Marquer comme payé
                      </button>
                    </td>
                  </tr>
                ))}
                {payouts.rows.length === 0 && <tr><td colSpan={6} className="p-16 text-center text-white/50">Aucun solde dû.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="border border-white/10">
            <div className="p-4 border-b border-white/10 text-xs uppercase tracking-widest text-white/40 bg-[#0A0A0A]">Historique des paiements</div>
            <table className="w-full text-sm">
              <tbody>
                {payouts.history.length === 0 ? (
                  <tr><td className="p-16 text-center text-white/50">Aucun paiement effectué.</td></tr>
                ) : payouts.history.map((h) => {
                  const artist = payouts.rows.find(r => r.artist_id === h.artist_id) || artists.find(a => a.id === h.artist_id);
                  return (
                    <tr key={h.id} className="border-b border-white/5">
                      <td className="p-4">{artist?.artist_name || artist?.name || h.artist_id}</td>
                      <td className="p-4 font-display text-xl">${h.amount.toFixed(2)}</td>
                      <td className="p-4 text-white/60">{h.order_count} commande(s)</td>
                      <td className="p-4 text-white/60">{h.method}{h.reference && ` · ${h.reference}`}</td>
                      <td className="p-4 text-white/40 text-xs">{new Date(h.paid_at).toLocaleString("fr-FR")}</td>
                      <td className="p-4"><Check size={16} className="text-green-400" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {payingRow && (
        <PayModal row={payingRow} onClose={() => setPayingRow(null)} onDone={() => { setPayingRow(null); fetchAll(); }} />
      )}

      {viewingOrder && (
        <OrderDetailsModal order={viewingOrder} onClose={() => setViewingOrder(null)} showAllItems={true} />
      )}
    </div>
  );
}
